import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query';
import {add, deleteRecord, fetch, update} from '../services/CustomersService';
import {Customer} from '../types';
import {notifyError, notifySuccess} from '../../../components/ServiceNotifications';

export const useCustomer = (customerId?: string) => {
    const queryClient = useQueryClient();

    const customerQuery = useQuery({
        queryKey: ['customer', customerId],
        queryFn: () => fetch(customerId!),
        enabled: !!customerId,
    });

    const createMutation = useMutation({
        mutationFn: (values: Customer) => {
            const payload = {...values} as Record<string, unknown>;
            delete payload.created;
            return add(payload as Customer);
        },
        onSuccess: () => {
            notifySuccess('Customer created');
            queryClient.invalidateQueries({queryKey: ['customers']});
        },
        onError: notifyError,
    });

    const updateMutation = useMutation({
        mutationFn: (values: Customer) => {
            if (!customerId) throw new Error('customerId is required parameter');
            return update(customerId, values);
        },
        onSuccess: () => {
            notifySuccess('Customer updated');
            queryClient.invalidateQueries({queryKey: ['customers']});
            queryClient.invalidateQueries({queryKey: ['customer', customerId]});
        },
        onError: notifyError,
    });

    const deleteMutation = useMutation({
        mutationFn: () => {
            if (!customerId) throw new Error('customerId is required parameter');
            return deleteRecord(customerId);
        },
        onSuccess: () => {
            notifySuccess('Customer deleted');
            queryClient.invalidateQueries({queryKey: ['customers']});
        },
        onError: notifyError,
    });

    return {
        record: customerQuery.data,
        loading: customerQuery.isLoading,
        fetchRecord: customerQuery.refetch,
        createRecord: createMutation.mutate,
        updateRecord: updateMutation.mutate,
        deleteRecord: deleteMutation.mutate,
        createMutation,
        updateMutation,
        deleteMutation,
    };
};
